import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  useTheme,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { FilterContext } from '../../contexts/FilterContext';
import fetchData from '../../helpers/fetchData';
import { tokens } from '../../theme';
import NewsletterAdInsertionDetailModal from './NewsletterAdInsertionDetailModal';
import NewsletterAdInsightsOverview, { OverviewSummary } from './NewsletterAdInsightsOverview';

interface DecisionPath {
  chain_type: string;
  tiers_before_match: string[];
  winning_tier: number;
  winning_tier_name: string;
}

interface Insertion {
  id: number;
  newsletter_type: string;
  newsletter_date: string | null;
  article_id: string;
  article_title: string;
  isin: string | null;
  ad_source: string;
  ad_copy: string;
  ad_link: string | null;
  banner: string | null;
  llm_model: string | null;
  ad_details: Record<string, any>;
  decision_path: DecisionPath;
  created_at: string | null;
  registrations: number;
  orders: number;
  sois: number;
  dois: number;
  revenue: number;
  recipients: number;
  opens: number;
  etkp: number;
  etko: number;
  // Backend dedupes AI-copy variants per banner-send-slot. variants_count
  // is the number of AI-generation runs collapsed into this row (≥ 1).
  variants_count?: number;
  variant_ids?: number[];
}

type Summary = OverviewSummary & {
  by_source: Record<string, number>;
  by_client: Record<string, number>;
  revenue_by_source?: Record<string, number>;
  revenue_by_client?: Record<string, number>;
};

const AD_SOURCE_LABELS: Record<string, string> = {
  webinar_default: 'Live Webinar',
  webinar_evergreen: 'Evergreen Webinar',
  plx_offer_time_limited: 'Direkt-Werbung',
  plx_offer_evergreen: 'PLX Evergreen',
  landing_page_fallback: 'VNR Fallback',
  landing_page_direct: 'VNR Direkt',
};

const AD_SOURCE_COLORS: Record<string, string> = {
  webinar_default: '#1976d2',
  webinar_evergreen: '#0288d1',
  plx_offer_time_limited: '#7b1fa2',
  plx_offer_evergreen: '#9c27b0',
  landing_page_fallback: '#ed6c02',
  landing_page_direct: '#d32f2f',
};

const WEBINAR_SOURCES = ['webinar_default', 'webinar_evergreen'];
const PLX_SOURCES = ['plx_offer_time_limited', 'plx_offer_evergreen'];
const VNR_SOURCES = ['landing_page_fallback', 'landing_page_direct'];

type SortableField =
  | 'newsletter_date'
  | 'newsletter_type'
  | 'article_title'
  | 'ad_source'
  | 'registrations'
  | 'orders'
  | 'sois'
  | 'dois'
  | 'revenue'
  | 'etkp'
  | 'etko';

const headerCellSx = {
  backgroundColor: '#e0e0e0 !important',
  color: 'black',
  fontWeight: 'bold',
} as const;

const NewsletterAdInsertionsTable: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState } = useContext(FilterContext)!;

  const [data, setData] = useState<Insertion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<Summary | null>(null);

  const [selectedNewsletterType, setSelectedNewsletterType] = useState<string>('all');
  const [selectedAdSource, setSelectedAdSource] = useState<string>('all');

  const [orderBy, setOrderBy] = useState<SortableField>('newsletter_date');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const [selectedInsertion, setSelectedInsertion] = useState<Insertion | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (!filterState?.startDate || !filterState?.endDate) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const params: Record<string, any> = {
          date_from: formatDate(filterState.startDate),
          date_to: formatDate(filterState.endDate),
        };

        if (selectedNewsletterType !== 'all') {
          params.newsletter_type = selectedNewsletterType;
        }
        if (selectedAdSource !== 'all') {
          params.ad_source = selectedAdSource;
        }

        const response = (await fetchData('/api/newsletter-ad-insertions', params, false)) as any;

        if (!cancelled && response.success && response.data) {
          const insertions = response.data.insertions || [];
          setData(insertions);
          setTotal(insertions.length);
          setSummary(response.data.summary || null);
        } else if (!cancelled) {
          setError(response.error || 'Unknown error');
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error('Error loading newsletter ad insertions:', err);
          setError(err.message || 'Failed to load data');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [filterState?.startDate, filterState?.endDate, selectedNewsletterType, selectedAdSource]);

  const handleNewsletterTypeChange = (event: SelectChangeEvent) => {
    setSelectedNewsletterType(event.target.value);
    setPage(0);
  };

  const handleAdSourceChange = (event: SelectChangeEvent) => {
    setSelectedAdSource(event.target.value);
    setPage(0);
  };

  const handleSort = (property: SortableField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedFullData = useMemo(() => {
    return [...data].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue === null || aValue === undefined) return order === 'asc' ? 1 : -1;
      if (bValue === null || bValue === undefined) return order === 'asc' ? -1 : 1;

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [data, order, orderBy]);

  const sortedData = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedFullData.slice(start, start + rowsPerPage);
  }, [sortedFullData, page, rowsPerPage]);

  const handleRowClick = (insertion: Insertion) => {
    setSelectedInsertion(insertion);
    setModalOpen(true);
  };

  const truncate = (text: string | null, maxLen: number) => {
    if (!text) return '-';
    return text.length > maxLen ? text.substring(0, maxLen) + '...' : text;
  };

  const newsletterTypeOptions = useMemo(() => {
    if (!summary?.by_client) return [];
    return Object.keys(summary.by_client).sort();
  }, [summary]);

  const isWebinar = (source: string) => WEBINAR_SOURCES.includes(source);
  const isPlx = (source: string) => PLX_SOURCES.includes(source);
  const isVnr = (source: string) => VNR_SOURCES.includes(source);

  const renderStat = (value: number, applicable: boolean) => {
    if (!applicable) return <span style={{ color: '#bbb' }}>-</span>;
    if (value === 0) return <span style={{ color: '#999' }}>0</span>;
    return <strong>{value}</strong>;
  };

  const formatEuro = (value: number) => {
    if (!value) return <span style={{ color: '#999' }}>0,00 €</span>;
    return <strong>{value.toFixed(2).replace('.', ',')} €</strong>;
  };

  const renderReach = (value: number, hasReach: boolean) => {
    if (!hasReach) return <span style={{ color: '#bbb' }}>-</span>;
    return <>{value.toFixed(2).replace('.', ',')} €</>;
  };

  if (loading && data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && data.length === 0) {
    return (
      <Box p={2}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Overview: KPI cards + chart + per-ad-type + AI quality + top lists */}
      <NewsletterAdInsightsOverview
        summary={summary}
        startDate={filterState?.startDate || null}
        endDate={filterState?.endDate || null}
      />

      {/* Filters */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Newsletter</InputLabel>
          <Select
            value={selectedNewsletterType}
            onChange={handleNewsletterTypeChange}
            label="Newsletter"
          >
            <MenuItem value="all">Alle</MenuItem>
            {newsletterTypeOptions.map((nl) => (
              <MenuItem key={nl} value={nl}>
                {nl}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Ad Quelle</InputLabel>
          <Select value={selectedAdSource} onChange={handleAdSourceChange} label="Ad Quelle">
            <MenuItem value="all">Alle</MenuItem>
            {Object.entries(AD_SOURCE_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {loading && <CircularProgress size={24} sx={{ alignSelf: 'center' }} />}
      </Box>

      {/* Summary Chips */}
      {summary && (
        <Box mb={2}>
          <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
            <Typography variant="caption" sx={{ alignSelf: 'center', mr: 1 }}>
              Nach Quelle:
            </Typography>
            {Object.entries(summary.by_source).map(([source, count]) => {
              const rev = summary.revenue_by_source?.[source] || 0;
              return (
                <Chip
                  key={source}
                  label={`${AD_SOURCE_LABELS[source] || source}: ${count} | ${rev.toFixed(2).replace('.', ',')} €`}
                  size="small"
                  sx={{
                    backgroundColor: AD_SOURCE_COLORS[source] || '#757575',
                    color: '#ffffff',
                  }}
                />
              );
            })}
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Typography variant="caption" sx={{ alignSelf: 'center', mr: 1 }}>
              Nach Newsletter:
            </Typography>
            {Object.entries(summary.by_client).map(([client, count]) => {
              const rev = summary.revenue_by_client?.[client] || 0;
              return (
                <Chip
                  key={client}
                  label={`${client}: ${count} | ${rev.toFixed(2).replace('.', ',')} €`}
                  size="small"
                  variant="outlined"
                />
              );
            })}
            <Chip
              label={`Gesamt: ${total} | ${(summary.total_revenue || 0).toFixed(2).replace('.', ',')} €`}
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
            {summary.avg_cpa_per_webinar_registration ? (
              <Tooltip
                title="Globaler 90-Tage-Durchschnitt (nicht-evergreen Webinare). Nur Referenz — der Webinar-Umsatz je Banner ist der CPA-Wert der zugeordneten Bestellungen."
                placement="top"
              >
                <Chip
                  label={`ØCPA/Reg: ${summary.avg_cpa_per_webinar_registration.toFixed(2).replace('.', ',')} €`}
                  size="small"
                  variant="outlined"
                  sx={{ cursor: 'help' }}
                />
              </Tooltip>
            ) : null}
          </Box>
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 700 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>
                <TableSortLabel
                  active={orderBy === 'newsletter_date'}
                  direction={orderBy === 'newsletter_date' ? order : 'asc'}
                  onClick={() => handleSort('newsletter_date')}
                >
                  Datum
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellSx}>
                <TableSortLabel
                  active={orderBy === 'newsletter_type'}
                  direction={orderBy === 'newsletter_type' ? order : 'asc'}
                  onClick={() => handleSort('newsletter_type')}
                >
                  Newsletter
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellSx}>
                <TableSortLabel
                  active={orderBy === 'article_title'}
                  direction={orderBy === 'article_title' ? order : 'asc'}
                  onClick={() => handleSort('article_title')}
                >
                  Artikel
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellSx}>
                <TableSortLabel
                  active={orderBy === 'ad_source'}
                  direction={orderBy === 'ad_source' ? order : 'asc'}
                  onClick={() => handleSort('ad_source')}
                >
                  Ad Quelle
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellSx}>Banner</TableCell>
              <TableCell sx={headerCellSx} align="right">
                <TableSortLabel
                  active={orderBy === 'registrations'}
                  direction={orderBy === 'registrations' ? order : 'asc'}
                  onClick={() => handleSort('registrations')}
                >
                  Registrierungen
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellSx} align="right">
                <TableSortLabel
                  active={orderBy === 'orders'}
                  direction={orderBy === 'orders' ? order : 'asc'}
                  onClick={() => handleSort('orders')}
                >
                  Bestellungen
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellSx} align="right">
                <TableSortLabel
                  active={orderBy === 'sois'}
                  direction={orderBy === 'sois' ? order : 'asc'}
                  onClick={() => handleSort('sois')}
                >
                  SOIs
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellSx} align="right">
                <TableSortLabel
                  active={orderBy === 'dois'}
                  direction={orderBy === 'dois' ? order : 'asc'}
                  onClick={() => handleSort('dois')}
                >
                  DOIs
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellSx} align="right">
                <Tooltip
                  title="Webinar: Reg. × 90T-ØCPA. PLX: Σ CPA-Wert je Bestellung (jede Anfo mit eigenem Wert). VNR: Σ vld.costs (tatsächliche VNR-Auszahlung)."
                  placement="top"
                >
                  <TableSortLabel
                    active={orderBy === 'revenue'}
                    direction={orderBy === 'revenue' ? order : 'asc'}
                    onClick={() => handleSort('revenue')}
                  >
                    Revenue
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell sx={headerCellSx} align="right">
                <Tooltip
                  title="Erlös pro 1000 Newsletter-Empfänger. Nenner ist Newsletter-weit, also identisch für alle Ads im selben Newsletter. Aktuell nur für finanztrends verfügbar (Iterable), sonst '-'."
                  placement="top"
                >
                  <TableSortLabel
                    active={orderBy === 'etkp'}
                    direction={orderBy === 'etkp' ? order : 'asc'}
                    onClick={() => handleSort('etkp')}
                  >
                    ETKP
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell sx={headerCellSx} align="right">
                <Tooltip
                  title="Erlös pro 1000 Öffnungen. Nenner ist Newsletter-weit. Aktuell nur für finanztrends verfügbar (Iterable), sonst '-'."
                  placement="top"
                >
                  <TableSortLabel
                    active={orderBy === 'etko'}
                    direction={orderBy === 'etko' ? order : 'asc'}
                    onClick={() => handleSort('etko')}
                  >
                    ETKO
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row) => (
              <TableRow
                key={row.id}
                hover
                onClick={() => handleRowClick(row)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell sx={{ color: 'black', whiteSpace: 'nowrap' }}>
                  {row.newsletter_date || '-'}
                </TableCell>
                <TableCell sx={{ color: 'black' }}>{row.newsletter_type}</TableCell>
                <TableCell sx={{ color: 'black', maxWidth: 250 }}>
                  {truncate(row.article_title, 60)}
                </TableCell>
                <TableCell>
                  <Chip
                    label={AD_SOURCE_LABELS[row.ad_source] || row.ad_source}
                    size="small"
                    sx={{
                      backgroundColor: AD_SOURCE_COLORS[row.ad_source] || '#757575',
                      color: '#ffffff',
                      fontSize: 11,
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    color: 'black',
                    fontFamily: 'monospace',
                    fontSize: 11,
                    maxWidth: 200,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <span>{truncate(row.banner, 35)}</span>
                    {row.variants_count && row.variants_count > 1 ? (
                      <Tooltip
                        title={`${row.variants_count} AI-generierte Copy-Varianten teilen sich diesen Banner-Slot. Conversions/Revenue sind Banner-Level (1× pro Send), unabhängig von der Variantenzahl.`}
                      >
                        <Chip
                          label={`× ${row.variants_count}`}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 18,
                            fontSize: 10,
                            color: '#666',
                            borderColor: '#bbb',
                            cursor: 'help',
                          }}
                        />
                      </Tooltip>
                    ) : null}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ color: 'black' }}>
                  {renderStat(row.registrations, isWebinar(row.ad_source))}
                </TableCell>
                <TableCell align="right" sx={{ color: 'black' }}>
                  {renderStat(row.orders, isPlx(row.ad_source))}
                </TableCell>
                <TableCell align="right" sx={{ color: 'black' }}>
                  {renderStat(row.sois, isVnr(row.ad_source))}
                </TableCell>
                <TableCell align="right" sx={{ color: 'black' }}>
                  {renderStat(row.dois, isVnr(row.ad_source))}
                </TableCell>
                <TableCell align="right" sx={{ color: 'black' }}>
                  {formatEuro(row.revenue)}
                </TableCell>
                <TableCell align="right" sx={{ color: 'black' }}>
                  {renderReach(row.etkp, row.recipients > 0)}
                </TableCell>
                <TableCell align="right" sx={{ color: 'black' }}>
                  {renderReach(row.etko, row.opens > 0)}
                </TableCell>
              </TableRow>
            ))}
            {sortedData.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ color: 'black' }}>
                  Keine Daten gefunden
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[50, 100, 200, 500]}
      />

      <NewsletterAdInsertionDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        insertion={selectedInsertion}
      />
    </Box>
  );
};

export default NewsletterAdInsertionsTable;
